import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthorizationService } from '@app/services/authorization.service';
import { PersonPortalState } from '@app/models/person-portal-state';
import { LocalStorageUtilities } from '@app/utilities/local-storage-utilities';
import { AgreementStatus } from "@app/enumerations/agreement-status";
import { CustomerAgreement } from '@app/models/customer-agreement';
import { CustomerDocument } from '@app/models/customer-document';
import { UseNotice } from '@app/models/use-notice';
import { Mxna } from '@app/models/mxna'
import { SpotPrice } from '@app/models/spot-price';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-site-agreement',
  templateUrl: './site-agreement.component.html',
  styleUrls: ['./site-agreement.component.css']
})
export class SiteAgreementComponent implements OnInit {

  static readonly TITLE_TEXT: string = "Monex Customer Portal";

  // Properties.
  public title: string = SiteAgreementComponent.TITLE_TEXT;
  public agreement: string
  private useNotice: UseNotice;

  constructor( 
    private router: Router,
    private authorizationService: AuthorizationService,
  ) { }

  ngOnInit() {
    // Resturn the latest use notice which will be displayed to the user.
    this.findNotice();
  }

  /**
   * Called when the person clicks the "Accept" button
   * 
   * Note that PersonPortalStateInitialize is initialized in AuthorizationService::saveWebAuthenticaton.
   * 
   * 2020-01-21: Added logic to persist the "Customer Agreement".
   * 
   */
  public onAcceptAgreement(): void {
    // Update person state object to reflect the person accepted the site use agreement.
    let personPortalState: PersonPortalState = LocalStorageUtilities.getPersonPortalState();
    personPortalState.acceptedAgreement = AgreementStatus.ACCEPTED;   
    LocalStorageUtilities.setPersonPortalState(personPortalState);
    // Create a new agreement object that will be saved to the database.
    let customerAgreement: CustomerAgreement = new CustomerAgreement();
    customerAgreement.co = "H";
    customerAgreement.acctNum = personPortalState.acctNum;
    customerAgreement.agreementDate = new Date();
    customerAgreement.webauthenticationId = personPortalState.webauthenticationId;
    customerAgreement.useNoticesId = this.useNotice;
    // Create new Customer Agreement record and post it to the database.        
    this.authorizationService.updateWebauthorizationWithCustomerAgreement(customerAgreement, personPortalState.webauthenticationId.id).subscribe({
      next: (webauthentication: any) => {
        // personPortalState now has a reference to CustomerAgreement.
        personPortalState.webauthenticationId = webauthentication;
        // Mxna
        this.authorizationService.findMxnaById(personPortalState.acctNum).subscribe({
          next: (mxna: Mxna) => {
            personPortalState.mxna = mxna;
            // Update current market value          
            this.authorizationService.findDelayedPrices().subscribe({
              next: (prices: SpotPrice[]) => {
                personPortalState.mxna.mxPosTrade.forEach(position => {
                  prices.forEach((price) => {
                    if (price.symbol === position.commodity.slice(0, 4).trim()) {
                      if (position.unitBal > 0) {
                        position.currentMktVal = price.last_bid;
                      } else {
                        position.currentMktVal = price.last_ask;
                      }
                      position.extendedMktVal = position.unitBal * position.currentMktVal;
                    }
                  });
                })
                // Retrieve all customer documents from the MySQL server.
                this.authorizationService.findDocuments(personPortalState.co, personPortalState.acctNum).subscribe({
                  next: (customerDocuments: CustomerDocument[]) => {                   
                    personPortalState.customerDocuments = customerDocuments;
                    // Store the PersonPortalState object in local storage to keep user logged in between page refreshes  
                    // TODO: Decide if clearing is necessary.                    
                    LocalStorageUtilities.clearPersonPortalState();
                    LocalStorageUtilities.setPersonPortalState(personPortalState);
                    this.router.navigate(['/main']);
                  },
                  error: (err: HttpErrorResponse) => {
                    LocalStorageUtilities.clearPersonPortalState();
                    LocalStorageUtilities.setPersonPortalState(personPortalState);
                    this.router.navigate(['/main']);
                  }                 
                });
              },
            });
            //this.router.navigate(['/main']);
          },
          error: (err: any) => {
            console.log(`Before findDelayedPrices: ${err}`);
          },
        })
      }
    });
  }

  /**
   * Person clicked the Cancel button, thus declining the site use agreement
   * 
   */
  public onShowLandingScreen(): void {
    // Update person state object to reflect the person accepted the site agreement.
    let personPortalState: PersonPortalState = LocalStorageUtilities.getPersonPortalState();
    personPortalState.loggedIn = false;
    personPortalState.acceptedAgreement = AgreementStatus.DECLINED;
    LocalStorageUtilities.setPersonPortalState(personPortalState);
    this.router.navigate(['/landing']);
  }

  /**
   * Called when a user is activating his account.  It is also
   * called when a new notice is added to the database.
   * 
   */
  public findNotice(): void {
    this.authorizationService.findLatestUseNotice()
      .subscribe({
        next: (useNotice: any) => {
          this.useNotice = useNotice;
          this.agreement = useNotice.contentHtml;
        },
      })
  }


}
